import {
    Card,
    CardActionArea,
    Box,
    Typography,
    Avatar,
} from '@mui/material';
import { useNavigate } from 'react-router';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

interface CardProps {
    item: {
        title: string;
        path: string;
        img: string;
        description: string;
    };
    site: "home" | "comercial";
}

const Cards = (props: CardProps) => {
    const navigate = useNavigate();
    const hasPath = Boolean(props.item.path);

    const navigateTo = () => {
        if (!props.item.path) return;
        navigate(`${props.item.path}`);
    };

    // Card para el Home
    if (props.site === "home") {
        return (
            <Card
                onClick={navigateTo}
                sx={{
                    minHeight: '200px',
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 3,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: hasPath ? 'pointer' : 'default',
                    opacity: hasPath ? 1 : 0.7,
                    '&:hover': {
                        transform: hasPath ? 'translateY(-8px)' : 'none',
                        boxShadow: hasPath ? '0 12px 24px rgba(0,0,0,0.15)' : 'none',
                        '& .card-overlay': {
                            opacity: hasPath ? 1 : 0,
                        },
                        '& .card-icon': {
                            transform: hasPath ? 'scale(1.1)' : 'none',
                        },
                        '& .arrow-icon': {
                            transform: hasPath ? 'translateX(8px)' : 'none',
                        }
                    },
                    '&:active': {
                        transform: hasPath ? 'translateY(-4px)' : 'none',
                    }
                }}
            >
                <CardActionArea sx={{ height: '100%', position: 'relative' }}>
                    {/* Fondo con gradiente */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            opacity: 0.9,
                        }}
                    />

                    {/* Overlay con hover */}
                    <Box
                        className="card-overlay"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.95) 0%, rgba(118, 75, 162, 0.95) 100%)',
                            opacity: 0,
                            transition: 'opacity 0.3s ease',
                        }}
                    />

                    {/* Contenido */}
                    <Box
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '200px',
                            p: 3,
                        }}
                    >
                        {/* Icono */}
                        <Avatar
                            className="card-icon"
                            sx={{
                                width: 80,
                                height: 80,
                                mb: 2,
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                backdropFilter: 'blur(10px)',
                                transition: 'transform 0.3s ease',
                            }}
                        >
                            <img
                                src={props.item.img}
                                alt={props.item.description}
                                style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                            />
                        </Avatar>

                        {/* Título */}
                        <Typography
                            variant="h6"
                            sx={{
                                color: 'white',
                                fontWeight: 700,
                                textAlign: 'center',
                                mb: 1,
                                textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            }}
                        >
                            {props.item.title}
                        </Typography>

                        {/* Flecha */}
                        <ArrowForwardIcon
                            className="arrow-icon"
                            sx={{
                                color: 'white',
                                transition: 'transform 0.3s ease',
                            }}
                        />
                    </Box>
                </CardActionArea>
            </Card>
        );
    }

    // Card para Comercial
    if (props.site === "comercial") {
        return (
            <Card
                onClick={navigateTo}
                sx={{
                    minWidth: '140px',
                    minHeight: '160px',
                    maxHeight: '180px',
                    borderRadius: 2,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: hasPath ? 'pointer' : 'default',
                    opacity: hasPath ? 1 : 0.7,
                    border: '1px solid #e0e0e0',
                    '&:hover': {
                        transform: hasPath ? 'scale(1.05)' : 'none',
                        boxShadow: hasPath ? '0 8px 16px rgba(0,0,0,0.12)' : 'none',
                        borderColor: hasPath ? '#173456' : '#e0e0e0',
                        '& .comercial-icon': {
                            transform: hasPath ? 'scale(1.15)' : 'none',
                        },
                        '& .comercial-title': {
                            color: hasPath ? '#173456' : '#333',
                        }
                    },
                    '&:active': {
                        transform: hasPath ? 'scale(1.02)' : 'none',
                    }
                }}
            >
                <CardActionArea sx={{ height: '100%' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 2,
                            height: '100%',
                        }}
                    >
                        {/* Icono */}
                        <Box
                            className="comercial-icon"
                            sx={{
                                width: props.item.description !== "delivered" ? '60px' : '70px',
                                height: props.item.description !== "delivered" ? '60px' : '70px',
                                mb: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#f5f5f5',
                                borderRadius: 2,
                                transition: 'transform 0.3s ease',
                            }}
                        >
                            <img
                                src={props.item.img}
                                alt={props.item.description}
                                style={{
                                    width: props.item.description !== "delivered" ? '40px' : '50px',
                                    height: props.item.description !== "delivered" ? '40px' : '50px',
                                    objectFit: 'contain',
                                }}
                            />
                        </Box>

                        {/* Título */}
                        <Typography
                            className="comercial-title"
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                textAlign: 'center',
                                color: '#333',
                                transition: 'color 0.3s ease',
                                lineHeight: 1.3,
                                fontSize: '0.875rem',
                            }}
                        >
                            {props.item.title}
                        </Typography>
                    </Box>
                </CardActionArea>
            </Card>
        );
    }

    return null;
};

export default Cards;